/**
 * metricRegistryApi.ts — thin typed client for /api/metric-registry
 * (scorecard rebuild P2). Lives OUTSIDE the frozen services/api.ts on the
 * workflowTestApi precedent (deferred-items 2026-06-03); fold into
 * api.ts at the next unlock if desired.
 */
import axios from 'axios';
import { installPracticeGuard } from './practice/practiceGuard';

const api = axios.create({ baseURL: '/api', withCredentials: true });
// A test meeting answers this client's writes in the tab (services/practice).
installPracticeGuard(api);

export type RegistrySourceKind = 'deals' | 'scheduling' | 'tickets' | 'time' | 'fathom' | 'derived';
export type RegistryAggregation = 'count' | 'sum' | 'average' | 'ratio' | 'percentage';
export type RegistryTargetMode = 'quarterly_total' | 'weekly_fixed' | 'weekly_floor' | 'weekly_ceiling';
export type RegistryClassification = 'leading' | 'lagging';

export interface RegistryOperand {
  kind: RegistrySourceKind;
  event: string;
  refs: number[];
}

export interface RegistryBinding {
  aggregation: RegistryAggregation;
  source?: RegistryOperand;
  operands?: { numerator: RegistryOperand; denominator: RegistryOperand };
  ownerBasis?: 'deal_owner' | 'stage_actor' | 'ticket_assignee' | 'time_user' | 'booking_primary_host' | 'none';
  onCallTaskIds?: number[];
  perUser?: boolean;
  /** scheduling 'discovery_unique': only count bookings hosted by these users. */
  hostUserIds?: number[];
  /** scheduling 'discovery_unique': a returning booker is NEW after this many days (default 30). */
  requalifyDays?: number;
}

export interface RegistryMetric {
  metric_id: number;
  name: string;
  description: string | null;
  unit: string | null;
  department_id: number | null;
  department_name: string | null;
  source_kind: RegistrySourceKind;
  binding: RegistryBinding;
  target_mode: RegistryTargetMode;
  classification: RegistryClassification;
  enabled: boolean;
}

export interface RegistrySources {
  pipelines: Array<{ id: number; name: string; stages: Array<{ key: string; label: string }> }>;
  eventTypes: Array<{ id: number; name: string; slug: string; status: string }>;
  timeTasks: Array<{ id: number; name: string }>;
  departments: Array<{ id: number; name: string }>;
  /** F2 - Financials (QuickBooks) measure catalog + live connection state. */
  qboConnected?: boolean;
  qboCompany?: string | null;
  qboMeasures?: Array<{ key: string; label: string; unit: string }>;
}

export interface CreateRegistryMetricPayload {
  name: string;
  description?: string | null;
  unit?: string | null;
  department_id?: number | null;
  classification: RegistryClassification;
  target_mode: RegistryTargetMode;
  target_value?: number | null;
  quarter?: number;
  year?: number;
  source_kind: RegistrySourceKind;
  binding: RegistryBinding;
}

// ── Widget batch + mini-scorecard clients (P6) ──────────────────────────
// Live here (unfrozen) rather than in the frozen services/api.ts — the
// workflowTestApi precedent. Fold into the typed client at the next
// api.ts unlock (deferred-items).

export interface WidgetAggregateItem {
  metricId: number;
  dateStart?: string;
  dateEnd?: string;
  bucketBy?: 'days' | 'weeks' | 'months' | 'years';
  calculation?: 'total' | 'average';
  comparisonPeriod?: 'previous_period' | 'previous_year' | 'none';
}

export interface WidgetAggregateResult {
  metricId: number;
  denied?: boolean;
  currentValue?: number;
  previousValue?: number | null;
  changePercent?: number | null;
  changeAbsolute?: number | null;
  trend?: 'up' | 'down' | 'flat';
  periodLabel?: string;
  previousPeriodLabel?: string | null;
  timeSeries?: Array<{ date: string; value: number; label: string }>;
  breakdown?: Array<{ label: string; value: number }>;
  metricUnit?: string | null;
  metricName?: string | null;
  isRateMetric?: boolean;
}

export interface ScorecardMiniRow {
  metric_id: number;
  denied?: boolean;
  missing?: boolean;
  name?: string;
  unit?: string | null;
  department_name?: string | null;
  classification?: 'leading' | 'lagging' | null;
  source_kind?: string | null;
  quarter?: number;
  year?: number;
  quarter_target?: number | null;
  quarter_actual?: number;
  target_type?: 'total' | 'threshold';
  threshold_direction?: 'below' | 'above';
  pace?: {
    mode: string;
    as_of_week: string;
    delta: number;
    on_pace: boolean;
    weeks_remaining: number;
    required_per_week_to_recover: number | null;
    required_this_week_to_recover: number | null;
  } | null;
  weeks?: Array<{ week_start: string; weekly_target: number; weekly_actual: number; is_on_track: boolean }>;
}

export const widgetBatchApi = {
  /** One round-trip for every metric widget on the board (AUD-036). */
  batchAggregate: (items: WidgetAggregateItem[]): Promise<WidgetAggregateResult[]> =>
    api.post('/widget-data/aggregate-multiple', { items }).then(res => res.data.data),

  /** One round-trip for every mini-scorecard widget on the board. */
  scorecardMini: (metricIds: number[]): Promise<ScorecardMiniRow[]> =>
    api.get('/widget-data/scorecard-mini', { params: { metricIds: metricIds.join(',') } }).then(res => res.data.data),
};

export const metricRegistryApi = {
  list: (): Promise<RegistryMetric[]> =>
    api.get('/metric-registry').then(res => res.data.data),

  sources: (): Promise<RegistrySources> =>
    api.get('/metric-registry/sources').then(res => res.data.data),

  create: (payload: CreateRegistryMetricPayload): Promise<{ metric_id: number; name: string; computed_weeks: number }> =>
    api.post('/metric-registry', payload).then(res => res.data.data),

  update: (metricId: number, payload: Partial<CreateRegistryMetricPayload> & { enabled?: boolean }): Promise<{ metric_id: number }> =>
    api.put(`/metric-registry/${metricId}`, payload).then(res => res.data.data),

  recompute: (metricId: number, lookbackDays?: number): Promise<{ weeks: number }> =>
    api.post(`/metric-registry/${metricId}/recompute`, { lookbackDays }).then(res => res.data.data),

  /** F1 — the accountability seat (lives here, not frozen api.ts). */
  setMetricOwner: (metricId: number, ownerUserId: number | null): Promise<{ metric_id: number; owner_user_id: number | null }> =>
    api.patch(`/metrics/${metricId}/owner`, { owner_user_id: ownerUserId }).then(res => res.data.data),

  /** F5 — current pollution findings for the hub badges (read-only). */
  sentinelFindings: (): Promise<Array<{ type: string; severity: 'high' | 'medium' | 'low'; metric_id: number | null; metric_name: string | null; detail: string }>> =>
    api.get('/scorecard/sentinel').then(res => res.data.data),

  /** F1 — manual Monday-digest trigger (force bypasses the daily guard). */
  runDigest: (force = false): Promise<{ notified: number; emailed: number; companyNotified: number; skippedAlreadySent: number; errors: string[] }> =>
    api.post('/scorecard/digest/run', { force }).then(res => res.data.data),
};
